/**
 * `/callback` — where the SSO round trip lands.
 *
 * Two obligations, both from SSO_INTEGRATION §3:
 *
 * 1. Exchange the code (the shell's `completeSsoCallback` does the PKCE half).
 * 2. **Restore the deep link.** Someone who opened `/documents/<id>` cold must
 *    sign in and arrive at that document, not on the home page (spec FR-2).
 *    `takeReturnTo` reads the path `SsoRedirect` stashed before it left.
 */

import { Button } from '@fiveox/ui'
import { completeSsoCallback, takeReturnTo } from '@fiveox/shell'
import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router'

import { RETURN_KEY, ssoConfig } from '../lib/sso-config'
import { session } from '../lib/session'

export function Callback() {
  const navigate = useNavigate()
  const [failed, setFailed] = useState(false)

  // The authorization code is single-use, and `completeSsoCallback` *consumes*
  // the PKCE verifier and state out of `sessionStorage` before exchanging it.
  // A second invocation therefore finds nothing to validate against and throws
  // `SSO callback state mismatch` — turning a sign-in that actually succeeded
  // into "could not be completed". StrictMode runs this effect twice in
  // development, so without the guard that was *every* dev sign-in: the first
  // run exchanged the code and auth answered 200, the first run's cleanup
  // discarded the tokens, and the second run painted the failure.
  //
  // The same ref, for the same reason, guards accounts' own /authorize page.
  // A cancellation flag cannot do this job: StrictMode's cleanup runs
  // *between* the two invocations, so it would void the one real exchange.
  const started = useRef(false)

  useEffect(() => {
    if (started.current) return
    started.current = true
    completeSsoCallback(ssoConfig())
      .then((tokens) => {
        session.storeTokens(tokens)
        // `replace`, not `push`: the callback URL carries a spent code and a
        // back button must not land on it.
        // `takeReturnTo` gives null when nothing was stashed — a direct
        // sign-in rather than a deep link. That lands on the app root.
        navigate(takeReturnTo(RETURN_KEY) ?? '/', { replace: true })
      })
      .catch(() => {
        setFailed(true)
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <div className="bg-background flex min-h-screen items-center justify-center px-6">
      {failed ? (
        <div className="max-w-sm text-center">
          <p className="text-foreground mb-4 text-sm">Sign-in could not be completed.</p>
          <Button type="button" onClick={() => navigate('/', { replace: true })}>
            Try again
          </Button>
        </div>
      ) : (
        <p className="text-muted-foreground text-sm">Signing you in…</p>
      )}
    </div>
  )
}
